import moment from 'moment';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import { canSeeWorkCalendars } from '../../../../../features/profile/entityPermissions';
import {setIndicatorLoading} from '../../../../../features/indicator';
import {
    getWorkCalendarIdByEmployeeId,
    getWorkCalendar,
    getWorkCalendars,
    setCurrentYear,
    setWorkCalendarId
} from '../../../../../features/settings/workcalendar';
import {useAppDispatch, useAppSelector} from '../../../../../hooks/hooks';
import {CalendarView, Size, Year} from '../../../../../components/shared/calendar/types';

const useWorkCalendarPage = (isMob: boolean) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const profile = useAppSelector((state) => state.profile);
    const canLoadWorkCalendars = canSeeWorkCalendars(profile);
    const { workCalendars, workCalendarId, year, currentYear, employeeWorkCalendarId } = useAppSelector((state) => state.workCalendar);
    const hasInitializedSelection = useRef(false);
    const [isBootstrapping, setIsBootstrapping] = useState(true);
    const [isCalendarLoading, setIsCalendarLoading] = useState(false);

    const hasCalendarSelection = canLoadWorkCalendars
        ? workCalendarId > 0
        : (employeeWorkCalendarId > 0 || workCalendarId > 0);

    const loading = isBootstrapping
        || isCalendarLoading
        || !hasCalendarSelection
        || !year
        || year.year < 1;

    const fetchCalendar = useCallback(async (id: number, selectedYear: number) => {
        setIsCalendarLoading(true);

        try {
            await dispatch(getWorkCalendar({ workCalendarId: id, year: selectedYear })).unwrap();
        } finally {
            setIsCalendarLoading(false);
        }
    }, [dispatch]);

    const calendarConfig = useMemo(() => {
        if (!year) {
            return null;
        }

        const years: Year[] = [{
            id: year.year,
            months: year.months.map((month) => ({
                id: month.month,
                days: month.days.map((day) => ({
                    id: day.day,
                    type: day.isShort ? 'short' : day.isHoliday ? 'off' : 'default'
                }))
            }))
        }];

        const minDate = moment({ year: year.year, month: 0, date: 1 }).startOf('day');
        const maxDate = moment({ year: year.year, month: 11, date: 31 }).startOf('day');
        const view: CalendarView = isMob ? 'month' : 'year';
        const size: Size = isMob ? 'small' : 'full';

        return { years, minDate, maxDate, view, size };
    }, [isMob, year]);

    const shouldRenderCalendar = Boolean(year && year.months.length > 0 && calendarConfig);

    useEffect(() => {
        let active = true;

        const bootstrap = async () => {
            setIsBootstrapping(true);

            try {
                if (!currentYear) {
                    dispatch(setCurrentYear(moment().year()));
                }

                if (canLoadWorkCalendars) {
                    await dispatch(getWorkCalendars()).unwrap();
                } else if (profile?.id) {
                    await dispatch(getWorkCalendarIdByEmployeeId({ employeeId: profile.id })).unwrap();
                }
            } finally {
                if (active) {
                    setIsBootstrapping(false);
                }
            }
        };

        void bootstrap();

        return () => {
            active = false;
        };
    }, [canLoadWorkCalendars, currentYear, dispatch, profile?.id]);

    useEffect(() => {
        if (hasInitializedSelection.current) {
            return;
        }

        const locationState = (location.state as { selectedCalendarId?: number } | null) ?? null;
        if (locationState?.selectedCalendarId) {
            dispatch(setWorkCalendarId(locationState.selectedCalendarId));
            hasInitializedSelection.current = true;
            navigate(location.pathname, { replace: true });
            return;
        }

        if (canLoadWorkCalendars) {
            if (workCalendars.length === 0) {
                return;
            }

            const rootCalendar = workCalendars.find((calendar) => calendar.isRoot);
            if (rootCalendar) {
                dispatch(setWorkCalendarId(rootCalendar.id));
                hasInitializedSelection.current = true;
            }
            return;
        }

        if (employeeWorkCalendarId > 0) {
            dispatch(setWorkCalendarId(employeeWorkCalendarId));
            hasInitializedSelection.current = true;
        }
    }, [canLoadWorkCalendars, dispatch, employeeWorkCalendarId, location.pathname, location.state, navigate, workCalendars]);

    useEffect(() => {
        if (workCalendarId < 1) {
            return;
        }

        void fetchCalendar(workCalendarId, currentYear);
    }, [currentYear, fetchCalendar, workCalendarId]);

    useEffect(() => {
        setIndicatorLoading(loading);

        return () => {
            setIndicatorLoading(false);
        };
    }, [loading]);

    return {
        workCalendars,
        workCalendarId,
        year,
        currentYear,
        loading,
        calendarConfig,
        shouldRenderCalendar,
    };
};

export default useWorkCalendarPage;
